import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from 'services/http.service';
import { FormModel } from 'services/models/form-builder/form.model';
import { ProbationaryPeriodSummaryModel } from 'services/models/probationary-period/probationary-period-summary.model';
import { ProbationaryPeriodModel } from 'services/models/probationary-period/probationary-period.model';
import { ServiceBase } from 'services/servicebase';
import { HTTP_CACHE_HEADERS } from 'utils/http-cache.constants';
import { ProbationaryMetricsParams } from 'app/views/pages/hiring/probationary-period/probationary-period-metrics/probationary-period-metrics-chart-filters';

type FinalizeFormData = {
  employeeId: string;
  assessmentType: string;
  formSubmissionId: string;
};

type SendFinalDecisionFormData = {
  employeeId: string;
  probationaryDecisionTypeId: string;
};

@Injectable()
export class ProbationaryPeriodService extends ServiceBase<ProbationaryPeriodModel> {
  constructor(http: HttpService) {
    super('probationaryperiod', http, environment.apiUrl);
  }

  createEntity(input: any): ProbationaryPeriodModel {
    return ProbationaryPeriodModel.create(input);
  }

  getSummary(summaryQueryString: string): Observable<ProbationaryPeriodSummaryModel> {
    const url = `${this.endpoint}/summary${summaryQueryString ? `?${summaryQueryString}` : ''}`;

    return this.http
      .get(url, HTTP_CACHE_HEADERS.NO_CACHE, null, null, true)
      .pipe(map((data: any) => ProbationaryPeriodSummaryModel.create(data.body)));
  }

  getMetrics(filters: ProbationaryMetricsParams | null = null): Observable<any> {
    let params = new HttpParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (value === null || value === undefined || value === '') {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== null && item !== undefined && item !== '') {
              params = params.append(key, item as string);
            }
          });
          return;
        }

        params = params.set(key, value as string);
      });
    }

    return this.http
      .get(`${this.endpoint}/metrics`, { params }, null, null, true)
      .pipe(map((data: any) => data.body));
  }

  exportData(filters: ProbationaryMetricsParams | null = null): Observable<ArrayBuffer> {
    let params = new HttpParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (value === null || value === undefined || value === '') {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== null && item !== undefined && item !== '') {
              params = params.append(key, item as string);
            }
          });
          return;
        }

        params = params.set(key, value as string);
      });
    }

    return this.http
      .get(
        `${this.endpoint}/data/export`,
        { params, responseType: 'arraybuffer' },
        this.baseUrl,
        undefined,
        true
      )
      .pipe(map((response: any) => response.body as ArrayBuffer));
  }

  getForm(period: number, formSubType: number): Observable<FormModel> {
    return this.http
      .get(
        `${this.endpoint}/form?period=${period}&formSubType=${formSubType}`,
        null,
        null,
        null,
        true
      )
      .pipe(map((data: any) => FormModel.create(data.body)));
  }

  finalizeForm(data: FinalizeFormData): Observable<any> {
    return this.http.post(`${this.endpoint}/assessment-status`, data, null, null, true);
  }

  sendFinalDecision(data: SendFinalDecisionFormData): Observable<any> {
    return this.http.post(`${this.endpoint}/final-decision`, data, null, null, true);
  }
}
